import { FormEvent, useMemo, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { formatPrice } from '../data/products';
import { useCart } from '../lib/cart';
import { StripeWordmark } from '../components/StripeWordmark';

export type CheckoutBuyer = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  companyName: string;
  companyEmail: string;
  companyPhone: string;
  companyState: string;
};

const BUYER_KEY = 'bizi_checkout_buyer_v1';

export function loadBuyerDraft(): Partial<CheckoutBuyer> {
  try {
    const raw = sessionStorage.getItem(BUYER_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

export function saveBuyerDraft(buyer: CheckoutBuyer) {
  sessionStorage.setItem(BUYER_KEY, JSON.stringify(buyer));
}

export function CheckoutDetailsPage() {
  const { items, subtotalCents, currency, ready } = useCart();
  const draft = useMemo(() => loadBuyerDraft(), []);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<CheckoutBuyer>({
    firstName: draft.firstName || '',
    lastName: draft.lastName || '',
    email: draft.email || '',
    phone: draft.phone || '',
    companyName: draft.companyName || '',
    companyEmail: draft.companyEmail || '',
    companyPhone: draft.companyPhone || '',
    companyState: draft.companyState || '',
  });

  if (!ready) {
    return (
      <div className="container checkout-page">
        <p className="muted">Loading your cart…</p>
      </div>
    );
  }

  if (items.length === 0) {
    return <Navigate to="/marketplace" replace />;
  }

  function update<K extends keyof CheckoutBuyer>(key: K, value: CheckoutBuyer[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      saveBuyerDraft(form);
      const res = await fetch('/api/create-checkout-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: items.map((item) => ({ productId: item.id, quantity: item.quantity })),
          buyer: form,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Unable to start Stripe checkout');
      if (!data.url) throw new Error('No Stripe checkout URL returned');
      window.location.href = data.url;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Checkout failed');
      setBusy(false);
    }
  }

  return (
    <div className="container checkout-page">
      <div className="page-hero checkout-page-hero">
        <span className="eyebrow">Checkout</span>
        <h1>Almost there</h1>
        <p className="muted">
          Confirm your details, then continue to Stripe™ for secure card payment and an itemized receipt.
        </p>
      </div>

      <div className="checkout-layout">
        <form className="card checkout-form-card" onSubmit={onSubmit}>
          <h2>Buyer information</h2>
          <p className="muted checkout-form-note">Required fields are marked with *</p>

          <div className="form-row">
            <label>
              First Name *
              <input
                required
                value={form.firstName}
                onChange={(e) => update('firstName', e.target.value)}
                autoComplete="given-name"
              />
            </label>
            <label>
              Last Name *
              <input
                required
                value={form.lastName}
                onChange={(e) => update('lastName', e.target.value)}
                autoComplete="family-name"
              />
            </label>
          </div>

          <div className="form-row">
            <label>
              Email *
              <input
                required
                type="email"
                value={form.email}
                onChange={(e) => update('email', e.target.value)}
                autoComplete="email"
              />
            </label>
            <label>
              Phone *
              <input
                required
                type="tel"
                value={form.phone}
                onChange={(e) => update('phone', e.target.value)}
                autoComplete="tel"
              />
            </label>
          </div>

          <h3 className="checkout-optional-title">Company details (optional)</h3>
          <div className="form-row">
            <label>
              Company Name
              <input
                value={form.companyName}
                onChange={(e) => update('companyName', e.target.value)}
                autoComplete="organization"
              />
            </label>
            <label>
              Company Email
              <input
                type="email"
                value={form.companyEmail}
                onChange={(e) => update('companyEmail', e.target.value)}
                autoComplete="email"
              />
            </label>
          </div>
          <div className="form-row">
            <label>
              Company Phone
              <input
                type="tel"
                value={form.companyPhone}
                onChange={(e) => update('companyPhone', e.target.value)}
                autoComplete="tel"
              />
            </label>
            <label>
              Company State
              <input
                value={form.companyState}
                onChange={(e) => update('companyState', e.target.value)}
                autoComplete="address-level1"
              />
            </label>
          </div>

          {error && <p className="buy-error">{error}</p>}

          <div className="checkout-form-actions">
            <Link className="btn btn-ghost" to="/marketplace">
              Back to Shopping
            </Link>
            <button className="btn btn-primary btn-stripe" type="submit" disabled={busy}>
              {busy ? (
                'Connecting to Stripe…'
              ) : (
                <>
                  Proceed to <StripeWordmark />
                </>
              )}
            </button>
          </div>
        </form>

        <aside className="card checkout-summary-card">
          <h2>Order summary</h2>
          <ul className="checkout-summary-lines">
            {items.map((item) => (
              <li key={item.id}>
                <div>
                  <strong>
                    {item.quantity}× {item.name}
                  </strong>
                  {item.version ? <span className="muted"> v{item.version}</span> : null}
                </div>
                <span>{formatPrice(item.priceCents * item.quantity, item.currency)}</span>
              </li>
            ))}
          </ul>
          <div className="checkout-summary-total">
            <span>Total due today</span>
            <strong>{formatPrice(subtotalCents, currency)}</strong>
          </div>
          <p className="muted checkout-summary-note">
            After Stripe™ payment succeeds, licenses are recorded and download links are issued for each extension.
          </p>
        </aside>
      </div>
    </div>
  );
}
